import { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { AppContexts } from "../../../providers";

const BuilderSendStep = ({ rental, onboarding, onSent }) => {
  const reservationContext = useContext(AppContexts.ReservationsContext);
  const reservationContextRef = useRef(reservationContext);
  reservationContextRef.current = reservationContext;
  const history = useHistory();
  const reservationId = onboarding?.guest_reservation_id;
  const [loadedReservation, setLoadedReservation] = useState(null);
  const reservation = reservationId &&
    String(loadedReservation?.guest_reservation_id) === String(reservationId)
    ? loadedReservation
    : null;
  const [loadingReservation, setLoadingReservation] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [message, setMessage] = useState("");
  const messageEditedRef = useRef(false);
  const [guide, setGuide] = useState(null);
  const guideUrl = guide?.reservationId === String(reservationId)
    ? guide.url
    : "";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setMessage("");
    messageEditedRef.current = false;
  }, [reservationId]);

  useEffect(() => {
    let active = true;
    setLoadedReservation(null);
    setGuide(null);
    setSent(false);
    setError("");
    setLoadingReservation(!!reservationId);
    if (!reservationId) return undefined;

    // The onboarding booking owns this send, regardless of the booking open
    // elsewhere in the app. Keep its data local rather than changing selection.
    const loadBooking = async () => {
      try {
        const response = await reservationContextRef.current
          .getSeletedReservation({ guest_reservation_id: reservationId });
        if (!active) return;
        if (String(response?.data?.guest_reservation_id) !== String(reservationId)) {
          throw new Error("Unexpected booking response");
        }
        setLoadedReservation(response.data);
      } catch {
        if (active) setError("Unable to reload the first booking. Please try again.");
      } finally {
        if (active) setLoadingReservation(false);
      }
    };
    loadBooking();
    return () => { active = false; };
  }, [reservationId, loadAttempt]);

  useEffect(() => {
    if (!reservation || (messageEditedRef.current && guideUrl)) return undefined;

    let active = true;
    const prepareGuideLink = async () => {
      let guestlinkId = reservation.guestlink_id;
      if (
        (!guestlinkId || String(guestlinkId) === "0") &&
        reservationContextRef.current.ensureGuestlink
      ) {
        guestlinkId = await reservationContextRef.current.ensureGuestlink(
          reservation,
        );
      }

      if (!guestlinkId || String(guestlinkId) === "0") {
        throw new Error("Unable to prepare the guest guide link.");
      }
      const url = `https://guide.ruebarue.com/guestbook/${guestlinkId}`;
      if (!active) return;

      setGuide({ reservationId: String(reservation.guest_reservation_id), url });
      if (!messageEditedRef.current) {
        setMessage(
          `Hi ${reservation.first_name || "there"}, here is your guide for ${
            rental?.name || "your stay"
          }: ${url}`,
        );
      }
    };

    prepareGuideLink().catch(() => {
      if (active) setError("Unable to prepare the guest guide link. Please try again.");
    });
    return () => {
      active = false;
    };
    // The context object is rebuilt after its own state updates. A ref keeps
    // this effect tied to the selected booking instead of repeatedly creating
    // the same guest link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rental?.name, rental?.rental_id, reservation]);

  const send = async () => {
    if (!reservation?.guest_reservation_id) {
      setError("Add a booking before sending the guide.");
      return;
    }
    if (!reservation.formatted_phone && !reservation.phone) {
      setError("This booking needs a mobile phone number.");
      return;
    }
    if (!message.trim()) {
      setError("Enter a message to send.");
      return;
    }
    if (!guideUrl) {
      setError("The guest guide link is still being prepared.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const result = await reservationContext.createSMS({
        guest_reservation_id: reservation.guest_reservation_id,
        message: message.trim(),
        phone: reservation.phone,
      });
      if (!result?.thread_id) {
        throw new Error("The message response did not include a thread.");
      }
      setSent(true);
      await onSent();
    } catch (sendError) {
      setError(
        sendError?.response?.data?.error ||
          "The guide was not sent. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="guide-onboarding-simple-panel">
      <p className="guide-onboarding-intro">
        Review the message before sending your published guide to your first
        guest.
      </p>
      {error && <p className="guide-onboarding-error" role="alert">{error}</p>}
      {error && !guideUrl && reservationId && (
        <button type="button" className="uk-button hms-btn" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
          Try again
        </button>
      )}
      {reservation ? (
        <div className="guide-onboarding-send-card">
          <p>
            <strong>To:</strong> {reservation.first_name} {reservation.last_name}
            <br />
            <span>{reservation.formatted_phone || reservation.phone}</span>
          </p>
          <label htmlFor="onboarding-send-message">Message</label>
          <textarea
            id="onboarding-send-message"
            className="uk-textarea"
            rows="5"
            value={message}
            onChange={(event) => {
              messageEditedRef.current = true;
              setMessage(event.target.value);
            }}
          />
          <button
            type="button"
            className="uk-button hms-btn hms-red-btn uk-margin-top"
            onClick={sent ? () => history.push("/") : send}
            disabled={loading || !guideUrl}
          >
            {sent
              ? "Guide sent — open dashboard"
              : loading
                ? "Sending…"
                : "Send guide"}
          </button>
        </div>
      ) : (
        <p className="secondary">
          {loadingReservation
            ? "Loading your first booking…"
            : "Add your first booking in the previous step to send this guide."}
        </p>
      )}
    </div>
  );
};

export default BuilderSendStep;
